import { type JSX, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import { PaperFormat } from '../../api/gql/graphql';
import { Header } from '../../components/accessible-components';
import { ImageNames, useImageSrc } from '../../services/asset-src-controller';
import { useScannerStore, useSessionStore } from '../../store';
import { AssistedOnboardingStage } from '../../types/types';
import { ScannerVariant } from '../scanner/utils';
import { OnboardingImagePage } from './components/onboarding-image-page';
import { OnboardingPaperSizeSelect } from './components/onboarding-paper-size-select';
import { OnboardingStartPage } from './components/onboarding-start-page';

interface AssistedOnboardingProps {
  onClose: () => void;
  onBack: () => void;
  onNext: () => void;
}

export function AssistedOnboarding({
  onClose,
  onBack,
  onNext,
}: AssistedOnboardingProps): JSX.Element {
  const { t } = useTranslation();
  const { paperType } = useSessionStore((store) => store);
  const { setPaperFormat } = useScannerStore((store) => store);
  const [assistedOnboardingView, setAssistedOnboardingView] = useState(
    AssistedOnboardingStage.HandOverPhone,
  );

  const rollUpPants = t('onboardingAssisted.rollPants');
  const removeShoes = t('onboardingAssisted.removeShoes');
  const standAsShown = <Trans>{t('onboardingAssisted.standAsShown')}</Trans>;

  const onBackClick = (): void => {
    switch (assistedOnboardingView) {
      case AssistedOnboardingStage.HandOverPhone: {
        onBack();
        break;
      }
      case AssistedOnboardingStage.RemoveShoes: {
        setAssistedOnboardingView(AssistedOnboardingStage.HandOverPhone);
        break;
      }
      case AssistedOnboardingStage.RollPants: {
        setAssistedOnboardingView(AssistedOnboardingStage.RemoveShoes);
        break;
      }
      case AssistedOnboardingStage.PaperSizeSelect: {
        setAssistedOnboardingView(AssistedOnboardingStage.RollPants);
        break;
      }

      case AssistedOnboardingStage.NextTakeShots: {
        setAssistedOnboardingView(
          paperType
            ? AssistedOnboardingStage.RollPants
            : AssistedOnboardingStage.PaperSizeSelect,
        );
        break;
      }
      case AssistedOnboardingStage.StandAsShown: {
        setAssistedOnboardingView(AssistedOnboardingStage.NextTakeShots);
        break;
      }
      case AssistedOnboardingStage.FirstCaptureInstruction: {
        setAssistedOnboardingView(AssistedOnboardingStage.StandAsShown);
        break;
      }
      default: {
        throw new Error('Invalid onboarding stage');
      }
    }
  };

  const getImage = useImageSrc(ScannerVariant.Assisted);
  const child = useMemo(() => {
    switch (assistedOnboardingView) {
      case AssistedOnboardingStage.HandOverPhone: {
        return (
          <OnboardingImagePage
            instruction={t('onboardingAssisted.handOverPhone')}
            imageSrc={getImage(ImageNames.HandOverPhone)}
            imageNumber={1}
            imageCount={4}
            buttonText={t('button.next')}
            altText={t('imageAlt.onboardingAssisted.handOverPhone')}
            onClick={() => {
              setAssistedOnboardingView(AssistedOnboardingStage.RemoveShoes);
            }}
          />
        );
      }
      case AssistedOnboardingStage.RemoveShoes: {
        return (
          <OnboardingImagePage
            instruction={removeShoes}
            imageSrc={getImage(ImageNames.TakeOfShoesImage)}
            imageNumber={2}
            imageCount={4}
            buttonText={t('button.next')}
            altText={t('imageAlt.onboardig.removeShoes')}
            onClick={() => {
              setAssistedOnboardingView(AssistedOnboardingStage.RollPants);
            }}
          />
        );
      }
      case AssistedOnboardingStage.RollPants: {
        return (
          <OnboardingImagePage
            instruction={rollUpPants}
            imageSrc={getImage(ImageNames.RollUpPantsImage)}
            imageNumber={3}
            imageCount={4}
            buttonText={t('button.next')}
            altText={t('imageAlt.onboardig.rollPants')}
            onClick={() => {
              switch (paperType) {
                case 'a4': {
                  setPaperFormat(PaperFormat.A4);
                  setAssistedOnboardingView(
                    AssistedOnboardingStage.NextTakeShots,
                  );
                  break;
                }
                case 'us-letter': {
                  setPaperFormat(PaperFormat.Letter);
                  setAssistedOnboardingView(
                    AssistedOnboardingStage.NextTakeShots,
                  );
                  break;
                }
                default: {
                  setAssistedOnboardingView(
                    AssistedOnboardingStage.PaperSizeSelect,
                  );
                  break;
                }
              }
            }}
          />
        );
      }
      case AssistedOnboardingStage.PaperSizeSelect: {
        return (
          <OnboardingPaperSizeSelect
            onClick={() => {
              setAssistedOnboardingView(AssistedOnboardingStage.NextTakeShots);
              usageTracker.track(UsageTrackerEvent.OnboardingScannerDetail, {
                scanner_type: 'assisted',
              });
            }}
            onClose={onClose}
            imageSrc={getImage(ImageNames.PlacePaperImage)}
            buttonText={t('button.next')}
            imageNumber={4}
            imageCount={4}
            altText={t('imageAlt.onboardig.paperPlacement')}
          />
        );
      }
      case AssistedOnboardingStage.NextTakeShots: {
        return (
          <OnboardingStartPage
            onClick={() => {
              setAssistedOnboardingView(AssistedOnboardingStage.StandAsShown);
            }}
            imageSrc={getImage(ImageNames.LandingPageImage)}
            buttonText={t('button.gotIt')}
            text={t('onboardingAssisted.takeTwoShots')}
            description={t('onboarding.twoShotsDescription')}
            altText={t('imageAlt.onboardig.twoShots')}
          />
        );
      }
      case AssistedOnboardingStage.StandAsShown: {
        return (
          <OnboardingImagePage
            instruction={standAsShown}
            imageSrc={getImage(ImageNames.PoseImage1)}
            imageNumber={1}
            imageCount={2}
            buttonText={t('button.next')}
            altText={t('imageAlt.onboardig.standAsShown')}
            onClick={() => {
              setAssistedOnboardingView(
                AssistedOnboardingStage.FirstCaptureInstruction,
              );
            }}
          />
        );
      }
      case AssistedOnboardingStage.FirstCaptureInstruction: {
        return (
          <OnboardingImagePage
            instruction={t('onboardingAssisted.firstCaptureInstruction')}
            imageSrc={getImage(ImageNames.FirstCaptureInstructionImage)}
            imageNumber={2}
            imageCount={2}
            buttonText={t('button.ready')}
            altText={t('imageAlt.onboardig.firstCaptureInstruction')}
            onClick={onNext}
          />
        );
      }
      default: {
        throw new Error('Invalid onboarding stage');
      }
    }
  }, [assistedOnboardingView, onClose]);
  return (
    <>
      <Header back={onBackClick} close={onClose} />
      {child}
    </>
  );
}
